import { Fragment } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { ThemePalette } from 'mui.theme';
import { type Asset } from 'types/asset.types';
import { getImageName } from 'utils/helpers/mapAssetImage';
import {
  addLiveDataToAssets,
  assetTypeMappings,
  getAssetTypeLabelKey,
} from 'views/FleetOverview/helpers/asset.helpers';
import { PNG } from 'components/Asset/PNG';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { IgnitionStatusIndicator } from 'components/StatusIndicator';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
export const AssetDetail = ({
  enableDarkTheme,
  selectedAsset,
  latestTagData,
  hierarchyInfo,
  injestedTime,
  assets,
}: {
  enableDarkTheme: boolean;
  selectedAsset: Asset;
  latestTagData: any;
  hierarchyInfo: any;
  injestedTime?: Record<string, string>;
  assets: Asset[];
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  const assetsWithLiveData = addLiveDataToAssets(assets, latestTagData);

  const assetDetails = [
    {
      label: t('asset_id'),
      value: selectedAsset.bumperNumber,
    },
    {
      label: t('loc_importer_col_type'),
      value: t(
        getAssetTypeLabelKey(assetTypeMappings[selectedAsset.assetType])
      ),
    },
    {
      label: t('hierarchy_node'),
      value: hierarchyInfo.nodeDisplay,
    },
    {
      label: 'OEM',
      value: selectedAsset.make,
    },
    {
      label: t('model_10'),
      value: selectedAsset.model,
    },
    {
      label: t('cdp_step_status'),
      value: selectedAsset.isActive
        ? t('operational_status')
        : t('out_of_service_status'),
    },
  ];

  return (
    <Box
      sx={{
        marginLeft: '10px',
        borderRadius: '5px',
        marginTop: '17px',
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.boxBackground,
        height: '100px',
        paddingTop: '5px',
        paddingLeft: '10px',
        color: palette.textPrimary,
      }}
    >
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 'bold',
        }}
      >
        {t('asset_details')}
        {injestedTime ? (
          <>
            <span
              style={{
                fontSize: '11px',
                paddingLeft: '10px',
                fontWeight: 'normal',
              }}
            >
              {Object.keys(injestedTime)[0]}
            </span>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '600',
                paddingLeft: '5px',
              }}
            >
              {Object.values(injestedTime)[0]}
            </span>
          </>
        ) : (
          <>
            <span
              style={{
                fontSize: '11px',
                paddingLeft: '10px',
                fontWeight: 'normal',
              }}
            >
              {t('last_injested_time')}
            </span>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '600',
                paddingLeft: '5px',
              }}
            >
              {Array.isArray(latestTagData)
                ? undefined
                : convertUtcToDisplayTime(
                    latestTagData[selectedAsset.device?.deviceId]?.ingestDate,
                    preferredTimeDisplayMode,
                    timezone,
                    DATE_TIME_FORMAT
                  )}
            </span>
          </>
        )}
      </Typography>
      <Box
        sx={{
          paddingTop: '10px',
        }}
      >
        <Grid2
          container
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          {/* Render asset details */}
          {assetDetails.map((data, index, arr) => {
            return (
              <Fragment key={`${data.label}-group`}>
                <Grid2 key={`${data.label}-1`} display="flex">
                  {index === 0 && (
                    <PNG
                      name={getImageName(selectedAsset, 'Small')}
                      style={{
                        height: '70px',
                        width: '90px',
                        marginLeft: '-20px',
                        position: 'relative',
                        zIndex: 1,
                      }}
                    />
                  )}
                  <div>
                    <Typography
                      sx={{
                        color: palette.textPrimary,
                        fontSize: '11px',
                        textAlign: 'center',
                        paddingBottom: '5px',
                      }}
                    >
                      {data.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: palette.textPrimary,
                        fontSize: '11px',
                        fontWeight: '600',
                        textAlign: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                        }}
                      >
                        {index === 0 && (
                          <IgnitionStatusIndicator
                            latestTagData={latestTagData}
                            asset={
                              assetsWithLiveData.find(
                                (asset) =>
                                  asset.bumperNumber === assetDetails[0].value
                              ) as Asset
                            }
                            size={10}
                            marginRight="5px"
                          />
                        )}
                        {data.value}
                      </Box>
                    </Typography>
                  </div>
                </Grid2>
                <Grid2 padding={0}>
                  {index < arr.length - 1 && (
                    <Divider
                      key={`${data.label}-divider`}
                      orientation="vertical"
                      sx={{
                        background: palette.borderGrey,
                      }}
                    />
                  )}
                </Grid2>
              </Fragment>
            );
          })}
        </Grid2>
      </Box>
    </Box>
  );
};
